import React, { useEffect } from "react";
import CheckoutIcon from "../../../../../../../images/common/link-blue.svg";
import EditIcon from "../../../../../../../images/products/edit-blue.svg";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import {
  ActiveInactiveButton,
  CustomLabel,
  ITableHeaders,
  Loader,
  TableWithHeaderButtons,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { GridNames, SystemItemTypes } from "../../../../shared/enums";
import {
  AccountSettingsStore,
  CheckOutStore,
  RolesAndPermissionStore,
} from "../../../../shared/store";
import { AuthService, CheckOutService } from "../../../../shared/services";
import { CheckoutRelatedGridModel } from "../../../../shared/store/CheckOutStore";
import {
  PlaceholderSmallerGrid,
  TableGrid,
} from "../../../../shared/components";
import { SystemColorsModel } from "../../../../shared/models";

type IProps = {
  itemType: keyof typeof SystemItemTypes;
  itemId: string;
  checkoutStore: CheckOutStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
};

const ItemCheckOutGrid: React.FC<IProps> = ({
  itemId,
  itemType,
  checkoutStore,
  navigate,
  rolesAndPermissionStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);

  useEffect(() => {
    loadData();
  }, []);

  function loadData() {
    CheckOutService.getCheckoutRelatedGridData(
      AuthService.authData.accountId,
      itemType,
      itemId
    ).subscribe({
      next: (response) => {
        setShowLoader(false);
        checkoutStore.deserializeAPIResponse(response.result || []);
      },
    });
  }
 
  function columns() {
    const columns: ITableHeaders<CheckoutRelatedGridModel>[] = [
      {
        dataKey: "isActive",
        title: "Status",
        onCellRender: (data: CheckoutRelatedGridModel) => {
          const colors = data.isActive
            ? SystemColorsModel.ligthGreenColors()
            : SystemColorsModel.lightGreyColors();
          return (
            <ActiveInactiveButton bgColor={colors.bgColor} color={colors.color}>
              {data.isActive ? "Active" : "Inactive"}
            </ActiveInactiveButton>
          );
        },
      },
      {
        dataKey: "name",
        title: "Name",
        className: "table-col-25",
        cellClassName: "table-col-25",
        onCellRender: (data: CheckoutRelatedGridModel) => {
          return <CustomLabel>{data.name}</CustomLabel>;
        },
      },
      {
        dataKey: "minPrice",
        title: "Price",
        onCellRender: (data: CheckoutRelatedGridModel) => {
          if (data.minPrice === data.maxPrice) {
            return (
              <CustomLabel>
                ${AccountSettingsStore.formatAmount(data.minPrice)}
              </CustomLabel>
            );
          }
          if (data.maxPrice === 0) {
            return (
              <CustomLabel>
                ${AccountSettingsStore.formatAmount(data.minPrice)}
              </CustomLabel>
            );
          }
          return (
            <CustomLabel>
              ${AccountSettingsStore.formatAmount(data.minPrice)} - $
              {AccountSettingsStore.formatAmount(data.maxPrice)}
            </CustomLabel>
          );
        },
      },
      {
        dataKey: "lastPurchase",
        title: "Last Purchase",
        onCellRender: (data: CheckoutRelatedGridModel) => {
          return <CustomLabel>{data.lastPurchase}</CustomLabel>;
        },
      },
      {
        dataKey: "lastPurchase",
        title: "Actions",
        className: "table-col-10 text-center",
        cellClassName: "table-col-10 text-center",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: CheckoutRelatedGridModel) => {
          return (
            <div>
              <img
                src={EditIcon}
                className="img-15 clickable"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
                  onEdit(data.id);
                }}
              />
            </div>
          );
        },
      },
    ];
    return columns;
  }

  if (showLoader) {
    return <> {new Loader(true).loaderComponent}</>;
  }

  const handleNewButtonClick = () => {
    if (!rolesAndPermissionStore.hasManagePermission("Create")) return;
    if (itemType === SystemItemTypes.Donation) {
      navigate(`/app/checkout/donation/${itemId}`);
    } else {
      navigate(`/app/checkout/detail`);
    }
  };

  const goToDetailPage = (id: string, name: string) => {
    navigate(`/app/checkouts/detail/${id}/${encodeURIComponent(name)}`);
  };

  if (checkoutStore.gridData.length == 0)
    return (
      <>
        <PlaceholderSmallerGrid
          title="No Checkouts"
          icon={CheckoutIcon}
          buttonCaption="New"
          disableButton={false}
          buttonClick={() => handleNewButtonClick()}
        />
      </>
    );

  function onEdit(id: string) {
    navigate(`/app/checkout/detail/${id}`);
  }

  return (
    <TableGrid
      wrapperClassName="table-with-header-btns"
      data={checkoutStore.gridData}
      headers={columns()}
      tableHeader={
        <TableWithHeaderButtons
          icon={CheckoutIcon}
          title="Checkout"
          totalItems={checkoutStore.gridData.length}
          isShowHeaderButton={true}
          onNewButtonClick={handleNewButtonClick}
          showRecentButton={false}
        />
      }
      totalItemCount={checkoutStore.gridData.length}
      cellSpacing={20}
      onRowClick={(data: CheckoutRelatedGridModel) =>
        goToDetailPage(data.id, data.name)
      }
      name={GridNames.Item}
    />
  );
};

export default withRouter(observer(ItemCheckOutGrid));
